import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import TopBar from "@/components/TopBar";
import Chart from "@/components/Chart";
import Sidebar from "@/components/Sidebar";
import AssetSelector from "@/components/AssetSelector";
import TradePanel from "@/components/TradePanel";
import PositionsPanel from "@/components/PositionsPanel";
import { api } from "@/lib/api";
import { subscribeTicker } from "@/lib/binance";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useNavigate } from "react-router-dom";
import {
  LineChart as LineChartIcon, ListOrdered, X, CandlestickChart,
  Activity, Volume2, VolumeX, Plus, Clock, TrendingUp, TrendingDown,
  Flame, Wallet as WalletIcon, LogOut, Globe, Shield,
  User as UserIcon, ChevronDown, Trophy, Users, Sun, Moon, PieChart
} from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { playTradeOpen, playTradeWin, playTradeLose, soundsEnabled, setSoundsEnabled } from "@/lib/sounds";

const INTERVALS = [
  { l: "1M",  v: "1m" },
  { l: "5M",  v: "5m" },
  { l: "15M", v: "15m" },
  { l: "1H",  v: "1h" },
  { l: "4H",  v: "4h" },
  { l: "1D",  v: "1d" },
];

// Animated price display with direction flash
function LivePriceDisplay({ price, digits, change24h, priceCellRef }) {
  const [dir, setDir] = useState("neutral");
  const prevPrice = useRef(price);

  useEffect(() => {
    if (prevPrice.current != null && price != null) {
      if (price > prevPrice.current) setDir("up");
      else if (price < prevPrice.current) setDir("down");
    }
    prevPrice.current = price;
    const t = setTimeout(() => setDir("neutral"), 600);
    return () => clearTimeout(t);
  }, [price]);

  const colorUp = "var(--iq-green)";
  const colorDown = "var(--iq-red)";
  const priceColor = dir === "up" ? colorUp : dir === "down" ? colorDown : "#fff";

  return (
    <div
      ref={priceCellRef}
      className="live-price-container"
    >
      {/* Price direction mini indicator */}
      <div style={{
        display: "flex", flexDirection: "column", gap: 1, opacity: dir === "neutral" ? 0.2 : 1,
        transition: "opacity 0.3s",
      }}>
        <div style={{
          width: 0, height: 0,
          borderLeft: "4px solid transparent", borderRight: "4px solid transparent",
          borderBottom: `5px solid ${dir === "up" ? colorUp : "rgba(255,255,255,0.15)"}`,
        }} />
        <div style={{
          width: 0, height: 0,
          borderLeft: "4px solid transparent", borderRight: "4px solid transparent",
          borderTop: `5px solid ${dir === "down" ? colorDown : "rgba(255,255,255,0.15)"}`,
        }} />
      </div>

      <div>
        <div style={{ fontSize: "9px", color: "var(--iq-text-dim)", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 2 }}>
          GÜNCEL FİYAT
        </div>
        <div
          data-testid="live-price"
          className={`live-price-value ${dir}`}
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: "16px", fontWeight: 800,
            color: priceColor,
            transition: "color 0.3s",
            textShadow: dir === "up"
              ? "0 0 12px rgba(0,230,118,0.7)"
              : dir === "down"
              ? "0 0 12px rgba(255,23,68,0.7)"
              : "none",
          }}
        >
          {price != null ? price.toFixed(digits) : "—"}
        </div>
      </div>

      {change24h != null && change24h !== 0 && (
        <div style={{
          fontSize: "11px", fontFamily: "'JetBrains Mono', monospace",
          color: change24h >= 0 ? "var(--iq-green)" : "var(--iq-red)",
          background: change24h >= 0 ? "var(--iq-green-dim)" : "var(--iq-red-dim)",
          padding: "2px 6px", borderRadius: "3px",
          fontWeight: 600,
        }}>
          {change24h >= 0 ? "+" : ""}{change24h.toFixed(2)}%
        </div>
      )}
    </div>
  );
}

export default function TradeRoom() {
  const { user, logout, switchMode } = useAuth();
  const { t, lang, setLang } = useI18n();
  const { theme, toggleTheme } = useTheme();
  const nav = useNavigate();
  const [assets, setAssets] = useState([]);
  const [current, setCurrent] = useState(null);
  const [interval, setIntervalV] = useState("1m");
  const [indicator, setIndicator] = useState("none");
  const [chartType, setChartType] = useState("area");
  const [soundOn, setSoundOn] = useState(soundsEnabled());
  const [tickers, setTickers] = useState({});
  const [openTrades, setOpenTrades] = useState([]);
  const [history, setHistory] = useState([]);
  const [mobileSheet, setMobileSheet] = useState(null);
  const [positionsExpanded, setPositionsExpanded] = useState(false);
  const [sidebarMenu, setSidebarMenu] = useState(null);
  const [openTabs, setOpenTabs] = useState([]);
  const [priceDir, setPriceDir] = useState({});   // symbol -> 'up' | 'down'
  const priceCellRef = useRef();
  const prevTickersRef = useRef({});

  useEffect(() => {
    api.get("/assets").then(({ data }) => {
      const list = Array.isArray(data) ? data : [];
      setAssets(list);
      if (!current && list.length) {
        setCurrent(list[0]);
        setOpenTabs([{ symbol: list[0].symbol, asset: list[0] }]);
      }
    }).catch(() => setAssets([]));
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!assets.length) return;
    const ws = subscribeTicker(assets.map((a) => ({ symbol: a.symbol, provider: a.provider })), (tk) => {
      setTickers((prev) => {
        const p = prev[tk.symbol]?.price;
        if (priceCellRef.current && current?.symbol === tk.symbol && p != null) {
          priceCellRef.current.classList.remove("flash-up", "flash-down");
          void priceCellRef.current.offsetWidth;
          priceCellRef.current.classList.add(tk.price >= p ? "flash-up" : "flash-down");
        }
        // Track per-tab price direction
        if (p != null) {
          setPriceDir(prev2 => ({
            ...prev2,
            [tk.symbol]: tk.price >= p ? "up" : "down",
          }));
          setTimeout(() => setPriceDir(prev2 => {
            const next = { ...prev2 };
            delete next[tk.symbol];
            return next;
          }), 500);
        }
        return { ...prev, [tk.symbol]: tk };
      });
    });
    return () => { try { ws.close(); } catch {} };
  }, [assets, current]);

  const openTradesRef = useRef([]);
  useEffect(() => { openTradesRef.current = openTrades; }, [openTrades]);

  const reloadTrades = async () => {
    const [o, h] = await Promise.all([api.get("/trades/open"), api.get("/trades/history")]);
    const prevOpenIds = new Set(openTradesRef.current.map((t) => t.id));
    const newClosed = h.data.filter((t) => prevOpenIds.has(t.id) && t.status === "closed");
    newClosed.forEach((t) => (Number(t.pnl) > 0 ? playTradeWin() : playTradeLose()));
    setOpenTrades(o.data);
    setHistory(h.data);
  };
  useEffect(() => { if (user) reloadTrades(); }, [user]);   // eslint-disable-line
  useEffect(() => {
    const hasBinary = openTrades.some((t) => t.type === "binary");
    const ms = hasBinary ? 200 : 1000;
    const i = setInterval(reloadTrades, ms);
    return () => clearInterval(i);
  }, [openTrades]);   // eslint-disable-line

  const assetsMap = useMemo(() => Object.fromEntries(assets.map((a) => [a.symbol, a])), [assets]);
  const currentPrice = tickers[current?.symbol]?.price ?? null;
  const change24h = tickers[current?.symbol]?.change ?? 0;
  const digits = current?.digits ?? 2;

  const selectAsset = (a) => {
    setCurrent(a);
    if (!openTabs.find(t => t.symbol === a.symbol)) {
      setOpenTabs(prev => [...prev, { symbol: a.symbol, asset: a }]);
    }
  };

  const closeTab = (symbol, e) => {
    e.stopPropagation();
    const newTabs = openTabs.filter(t => t.symbol !== symbol);
    if (newTabs.length === 0) return;
    setOpenTabs(newTabs);
    if (current?.symbol === symbol) {
      setCurrent(newTabs[newTabs.length - 1].asset);
    }
  };

  const currentSymbolTrades = openTrades.filter(t => t.symbol === current?.symbol);

  return (
    <div
      className="h-screen text-white flex flex-col overflow-hidden trade-bg"
      style={{ background: "var(--iq-bg)" }}
    >
      <div className="hidden lg:block"><TopBar /></div>

      {/* ─── Asset Tabs Bar ─── */}
      <div style={{
        height: "var(--iq-tabs-h)",
        display: "flex",
        alignItems: "stretch",
        borderBottom: "1px solid var(--iq-border)",
        background: "var(--iq-panel)",
        overflow: "hidden",
        flexShrink: 0,
        position: "relative",
      }}>
        {/* Subtle top shimmer line */}
        <div style={{
          position: "absolute", top: 0, left: 0, right: 0, height: "1px",
          background: "linear-gradient(90deg, transparent, rgba(0,230,118,0.2), transparent)",
          pointerEvents: "none",
        }} />

        {/* Asset selector popover button */}
        <AssetSelector assets={assets} current={current} tickers={tickers} onSelect={selectAsset} />

        {/* Open tabs */}
        <div className="iq-tabs-scroll" style={{ display: "flex", flex: 1, overflowX: "auto", overflowY: "hidden", alignItems: "stretch" }}>
          {openTabs.map((tab) => {
            const isActive = current?.symbol === tab.symbol;
            const tk = tickers[tab.symbol];
            const asset = assetsMap[tab.symbol];
            const tabDir = priceDir[tab.symbol];
            return (
              <button
                key={tab.symbol}
                className={`iq-asset-tab ${isActive ? "active" : ""}`}
                onClick={() => setCurrent(tab.asset)}
                data-testid={`tab-${tab.symbol}`}
              >
                {/* Active indicator dot */}
                {isActive && (
                  <span style={{
                    width: 5, height: 5, borderRadius: "50%",
                    background: "var(--iq-green)",
                    boxShadow: "0 0 6px var(--iq-green)",
                    flexShrink: 0,
                  }} />
                )}
                <span style={{ fontWeight: isActive ? 700 : 500 }}>{asset?.name || tab.symbol}</span>
                <span style={{ fontSize: "9px", color: "var(--iq-text-dim)" }}>
                  {asset?.category === "crypto" ? "Dijital" : "Blitz"}
                </span>
                {tk?.price != null && (
                  <span style={{
                    fontSize: "10px",
                    fontFamily: "'JetBrains Mono', monospace",
                    color: (tk.change ?? 0) >= 0 ? "var(--iq-green)" : "var(--iq-red)",
                    transition: "color 0.3s",
                    fontWeight: 600,
                  }}>
                    {(tk.change ?? 0) >= 0 ? "+" : ""}{(tk.change ?? 0).toFixed(2)}%
                  </span>
                )}
                {/* Price direction flash on tab */}
                {tabDir && (
                  <span style={{
                    fontSize: 9,
                    color: tabDir === "up" ? "var(--iq-green)" : "var(--iq-red)",
                    animation: "num-change 0.3s ease-out",
                  }}>
                    {tabDir === "up" ? "▲" : "▼"}
                  </span>
                )}
                {openTabs.length > 1 && (
                  <span className="close-btn" onClick={(e) => closeTab(tab.symbol, e)}>×</span>
                )}
              </button>
            );
          })}
          {/* Add new asset tab */}
          <button
            style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              padding: "0 12px", color: "var(--iq-text-dim)", background: "transparent",
              border: "none", cursor: "pointer", fontSize: "16px",
              transition: "color 0.2s",
            }}
            onMouseEnter={e => e.currentTarget.style.color = "#fff"}
            onMouseLeave={e => e.currentTarget.style.color = "var(--iq-text-dim)"}
          >
            <Plus style={{ width: 13, height: 13 }} />
          </button>
        </div>

        {/* Right side: live price with animation */}
        <LivePriceDisplay
          price={currentPrice}
          digits={digits}
          change24h={change24h}
          priceCellRef={priceCellRef}
        />
      </div>

      {/* ─── Main Layout ─── */}
      <div style={{ flex: 1, display: "flex", minHeight: 0, overflow: "hidden", position: "relative" }}>
        {/* ─── Trade Overlay ─── */}
        {currentSymbolTrades.length > 0 && (
          <TradeOverlay
            trades={currentSymbolTrades}
            tickers={tickers}
            current={current}
            assetsMap={assetsMap}
          />
        )}

        {/* Left Sidebar */}
        <div style={{ position: "relative", flexShrink: 0, zIndex: sidebarMenu ? 50 : 1 }}>
          <Sidebar
            onMenuClick={(id) => setSidebarMenu(id === null ? null : (sidebarMenu === id ? null : id))}
            activeMenu={sidebarMenu}
            symbol={current?.symbol}
            openTrades={openTrades}
            history={history}
          />
        </div>

        {/* Chart Area */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, minHeight: 0 }}>
          {/* Chart toolbar */}
          <div style={{
            display: "flex", alignItems: "center", gap: "4px",
            padding: "4px 10px",
            background: "var(--iq-panel)",
            borderBottom: "1px solid var(--iq-border)",
            flexShrink: 0,
            height: 34,
          }}>
            {/* Interval buttons */}
            <div style={{ display: "flex", alignItems: "center", gap: "2px" }}>
              {INTERVALS.map((iv) => (
                <button
                  key={iv.v}
                  data-testid={`iv-${iv.v}`}
                  onClick={() => setIntervalV(iv.v)}
                  className={`iv-btn ${interval === iv.v ? "active" : ""}`}
                >
                  {iv.l}
                </button>
              ))}
            </div>

            <div style={{ width: 1, height: 16, background: "var(--iq-border)", margin: "0 4px", flexShrink: 0 }} />

            <div style={{ flex: 1 }} />

            {/* Indicator dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="indicator-menu"
                  className="iv-btn"
                  style={{ display: "flex", alignItems: "center", gap: "4px" }}
                >
                  <LineChartIcon style={{ width: 11, height: 11 }} />
                  {indicator.toUpperCase()}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-panel border-line text-white" align="end">
                {["none", "rsi", "macd"].map((i) => (
                  <DropdownMenuItem
                    key={i} data-testid={`indicator-${i}`}
                    onClick={() => setIndicator(i)}
                    className="focus:bg-panel-hover cursor-pointer uppercase font-mono text-xs"
                  >{i}</DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Chart type toggle */}
            <button
              data-testid="chart-type-toggle"
              onClick={() => setChartType((c) => c === "area" ? "candles" : "area")}
              className={`iv-btn ${chartType !== "area" ? "active" : ""}`}
              title={chartType === "area" ? "Switch to candles" : "Switch to line"}
            >
              {chartType === "area"
                ? <CandlestickChart style={{ width: 13, height: 13 }} />
                : <Activity style={{ width: 13, height: 13 }} />}
            </button>

            {/* Sound toggle */}
            <button
              data-testid="sound-toggle"
              onClick={() => { const v = !soundOn; setSoundOn(v); setSoundsEnabled(v); }}
              className={`iv-btn ${soundOn ? "active" : ""}`}
              title="Toggle sounds"
            >
              {soundOn
                ? <Volume2 style={{ width: 13, height: 13 }} />
                : <VolumeX style={{ width: 13, height: 13 }} />}
            </button>
          </div>

          {/* Chart container */}
          <div style={{ flex: 1, position: "relative", minHeight: 0 }}>
            {current && (
              <Chart
                asset={current}
                interval={interval}
                indicator={indicator}
                chartType={chartType}
                openTrades={openTrades}
                currentPrice={currentPrice}
              />
            )}
            <PnlOverlay
              openTrades={openTrades}
              current={current}
              tickers={tickers}
              assetsMap={assetsMap}
            />
          </div>

          {/* Bottom positions bar */}
          <div className="hidden lg:block">
            <PositionsPanel
              openTrades={openTrades}
              history={history}
              tickers={tickers}
              onClosed={reloadTrades}
              assetsMap={assetsMap}
              expanded={positionsExpanded}
              onToggle={() => setPositionsExpanded(!positionsExpanded)}
            />
          </div>
        </div>

        {/* Right Trade Panel (desktop) */}
        <div className="hidden lg:flex" style={{ flexShrink: 0 }}>
          <TradePanel asset={current} price={currentPrice} onTradeOpened={reloadTrades} />
        </div>
      </div>

      {/* ─── Mobile FAB — opens trade sheet ─── */}
      <div className="lg:hidden fixed z-30" style={{
        bottom: "calc(var(--bottom-nav-h) + var(--safe-bottom) + 12px)",
        right: 16,
      }}>
        {/* Positions counter badge */}
        {openTrades.length > 0 && (
          <button data-testid="mobile-open-positions" onClick={() => setMobileSheet("positions")}
            className="touch-ripple flex items-center justify-center"
            style={{
              position: "absolute", bottom: 60, right: 0,
              width: 44, height: 44, borderRadius: "50%",
              background: "var(--iq-bg2)",
              border: "1px solid var(--iq-border-bright)",
              color: "var(--iq-text)", cursor: "pointer",
              boxShadow: "0 2px 12px rgba(0,0,0,0.3)",
            }}>
            <ListOrdered style={{ width: 18, height: 18 }} />
            <span style={{
              position: "absolute", top: -2, right: -2,
              background: "var(--iq-green)", color: "#000",
              fontSize: 8, fontWeight: 800, borderRadius: "50%",
              width: 18, height: 18, display: "flex",
              alignItems: "center", justifyContent: "center",
            }}>{openTrades.length}</span>
          </button>
        )}
        <button data-testid="mobile-open-trade" onClick={() => setMobileSheet("trade")}
          className="touch-ripple flex items-center justify-center"
          style={{
            width: 52, height: 52, borderRadius: "50%",
            background: "linear-gradient(135deg, #00e676, #00c853)",
            border: "none",
            color: "#000", cursor: "pointer",
            boxShadow: "0 4px 20px rgba(0,230,118,0.4)",
          }}>
          <LineChartIcon style={{ width: 22, height: 22 }} />
        </button>
      </div>

      {/* ─── Mobile bottom sheet ─── */}
      {mobileSheet && (
        <div className="lg:hidden fixed inset-0 z-40" style={{ background: "rgba(0,0,0,0.7)" }} onClick={() => setMobileSheet(null)}>
          <div
            className="sheet-enter safe-bottom"
            style={{
              position: "absolute", bottom: 0, left: 0, right: 0,
              background: "var(--iq-panel)", borderTop: "1px solid var(--iq-border)",
              maxHeight: "85vh", display: "flex", flexDirection: "column",
              borderRadius: "12px 12px 0 0",
              boxShadow: "0 -8px 40px rgba(0,0,0,0.6)",
              paddingBottom: "calc(var(--safe-bottom))",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-line touch-target">
              <span className="text-[11px] uppercase tracking-widest font-bold text-muted">
                {mobileSheet === "trade" ? t("trade") : mobileSheet === "positions" ? t("open_positions") : t("menu")}
              </span>
              <button data-testid="mobile-sheet-close" onClick={() => setMobileSheet(null)}
                className="p-1.5 text-muted hover:text-white opacity-60">
                <X style={{ width: 16, height: 16 }} />
              </button>
            </div>
            <div style={{ flex: 1, overflow: "auto" }}>
              {mobileSheet === "trade" ? (
                <TradePanel asset={current} price={currentPrice} onTradeOpened={() => { reloadTrades(); setMobileSheet(null); }} />
              ) : mobileSheet === "positions" ? (
                <PositionsPanel
                  openTrades={openTrades}
                  history={history}
                  tickers={tickers}
                  onClosed={reloadTrades}
                  assetsMap={assetsMap}
                  expanded={true}
                  onToggle={() => {}}
                />
              ) : (
                /* ─── Mobile Menu Sheet ─── */
                <div style={{ padding: "8px 0" }}>
                  {/* Account Switcher */}
                  <div style={{ padding: "12px 18px", borderBottom: "1px solid var(--iq-border)" }}>
                    <div style={{ fontSize: 9, color: "var(--iq-text-dim)", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 10 }}>Hesap</div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button
                        onClick={() => switchMode("demo")}
                        style={{
                          flex: 1, padding: "10px 12px", borderRadius: 6, cursor: "pointer",
                          border: user?.account_mode === "demo" ? "1px solid var(--iq-orange)" : "1px solid var(--iq-border)",
                          background: user?.account_mode === "demo" ? "rgba(255,152,0,0.1)" : "var(--iq-bg)",
                          display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 3,
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                          <span style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--iq-orange)", display: "inline-block" }} />
                          <span style={{ fontSize: 10, fontWeight: 700, color: "var(--iq-orange)", textTransform: "uppercase", letterSpacing: "0.08em" }}>Demo</span>
                        </div>
                        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13, fontWeight: 700, color: "#fff" }}>
                          ${Number(user?.demo_balance ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </button>
                      <button
                        onClick={() => switchMode("real")}
                        style={{
                          flex: 1, padding: "10px 12px", borderRadius: 6, cursor: "pointer",
                          border: user?.account_mode === "real" ? "1px solid var(--iq-green)" : "1px solid var(--iq-border)",
                          background: user?.account_mode === "real" ? "rgba(0,230,118,0.1)" : "var(--iq-bg)",
                          display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 3,
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                          <span style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--iq-green)", display: "inline-block" }} />
                          <span style={{ fontSize: 10, fontWeight: 700, color: "var(--iq-green)", textTransform: "uppercase", letterSpacing: "0.08em" }}>Gerçek</span>
                        </div>
                        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13, fontWeight: 700, color: "#fff" }}>
                          ${Number(user?.real_balance ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </button>
                    </div>
                  </div>

                  {/* Navigation Links */}
                  <div style={{ padding: "8px 0", borderBottom: "1px solid var(--iq-border)" }}>
                    {[
                      { to: "/wallet", label: t("wallet"), Icon: WalletIcon },
                      { to: "/portfolio", label: t("portfolio"), Icon: PieChart },
                      { to: "/tournaments", label: t("tournaments"), Icon: Trophy },
                      { to: "/leaderboard", label: t("leaderboard"), Icon: Trophy },
                      { to: "/social", label: t("social"), Icon: Users },
                      { to: "/profile", label: t("profile"), Icon: UserIcon },
                      { to: "/referrals", label: t("referrals"), Icon: Globe },
                      ...(user?.role === "admin" ? [{ to: "/admin", label: t("admin"), Icon: Shield }] : []),
                    ].map(({ to, label, Icon }) => (
                      <button
                        key={to}
                        onClick={() => { nav(to); setMobileSheet(null); }}
                        className="touch-ripple"
                        style={{
                          width: "100%", display: "flex", alignItems: "center", gap: 12,
                          padding: "14px 18px", background: "transparent", border: "none",
                          color: "var(--iq-text-bright)", cursor: "pointer",
                          fontSize: 13, fontWeight: 500, textAlign: "left",
                          minHeight: 48,
                        }}
                      >
                        <Icon style={{ width: 18, height: 18, color: "var(--iq-text-dim)", flexShrink: 0 }} />
                        {label}
                      </button>
                    ))}
                  </div>

                  {/* Language and Theme Selector */}
                  <div style={{ padding: "12px 18px", borderBottom: "1px solid var(--iq-border)" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                      <div style={{ fontSize: 9, color: "var(--iq-text-dim)", textTransform: "uppercase", letterSpacing: "0.12em" }}>Tercihler</div>
                      <button 
                        onClick={toggleTheme} 
                        style={{ background: "transparent", border: "none", color: "var(--iq-text)", cursor: "pointer" }}
                      >
                        {theme === "light" ? <Moon style={{ width: 16, height: 16 }} /> : <Sun style={{ width: 16, height: 16 }} />}
                      </button>
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      {[{c:"tr",n:"TR"},{c:"en",n:"EN"},{c:"es",n:"ES"},{c:"de",n:"DE"}].map((l) => (
                        <button
                          key={l.c}
                          onClick={() => setLang(l.c)}
                          style={{
                            padding: "7px 14px", borderRadius: 5,
                            border: lang === l.c ? "1px solid var(--iq-green)" : "1px solid var(--iq-border)",
                            background: lang === l.c ? "rgba(0,230,118,0.1)" : "var(--iq-bg)",
                            color: lang === l.c ? "var(--iq-green)" : "var(--iq-text)",
                            fontFamily: "'JetBrains Mono', monospace",
                            fontSize: 11, fontWeight: 700, cursor: "pointer",
                          }}
                        >{l.n}</button>
                      ))}
                    </div>
                  </div>

                  {/* Logout */}
                  <button
                    data-testid="mobile-logout"
                    onClick={async () => { await logout(); nav("/"); setMobileSheet(null); }}
                    style={{
                      width: "100%", display: "flex", alignItems: "center", gap: 12,
                      padding: "14px 18px", background: "transparent", border: "none",
                      color: "var(--iq-red)", cursor: "pointer",
                      fontSize: 13, fontWeight: 600, textAlign: "left",
                    }}
                  >
                    <LogOut style={{ width: 18, height: 18, flexShrink: 0 }} />
                    {t("logout")}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Trade Overlay ───
function timeLeft(iso) {
  if (!iso) return "";
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "0:00";
  const s = Math.ceil(ms / 1000);
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), r = s % 60;
  return h > 0
    ? `${h}:${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`
    : `${m}:${String(r).padStart(2, "0")}`;
}

function PnlOverlay({ openTrades, current, tickers, assetsMap }) {
  const [, force] = useState(0);
  useEffect(() => {
    const i = setInterval(() => force((v) => v + 1), 1000);
    return () => clearInterval(i);
  }, []);

  const relevant = (openTrades || []).filter((t) => t.symbol === current?.symbol);
  if (!relevant.length) return null;
  
  const price = tickers[current?.symbol]?.price;

  return (
    <div style={{
      position: "absolute",
      top: "10px",
      right: "60px",
      zIndex: 10,
      display: "flex",
      flexDirection: "column",
      gap: "8px",
      pointerEvents: "none",
    }}>
      {relevant.map((t) => {
        let pnl = 0;
        if (price != null) {
          if (t.type === "cfd") {
            const ret = (price - t.entry_price) / t.entry_price;
            const signed = t.direction === "sell" ? -ret : ret;
            pnl = signed * t.amount * (t.leverage || 1);
            if (pnl < -t.amount) pnl = -t.amount;
          } else {
            const win = (t.direction === "call" && price > t.entry_price) || (t.direction === "put" && price < t.entry_price);
            const payout = assetsMap[t.symbol]?.payout ?? 85;
            pnl = win ? t.amount * (payout / 100) : -t.amount;
          }
        }
        const roi = t.amount ? (pnl / t.amount) * 100 : 0;
        const isUp = t.direction === "call" || t.direction === "buy";
        const winning = pnl >= 0;
        const remaining = timeLeft(t.expires_at);

        return (
          <div
            key={t.id}
            data-testid={`pnl-overlay-${t.id}`}
            style={{
              minWidth: "160px",
              padding: "10px 14px",
              borderRadius: "8px",
              border: `1px solid ${winning ? "rgba(0, 230, 118, 0.35)" : "rgba(255, 23, 68, 0.35)"}`,
              background: winning ? "rgba(0, 230, 118, 0.12)" : "rgba(255, 23, 68, 0.12)",
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)",
              color: winning ? "var(--iq-green)" : "var(--iq-red)",
              fontFamily: "'JetBrains Mono', monospace",
              boxShadow: winning 
                ? "0 4px 20px rgba(0, 230, 118, 0.15), inset 0 1px 0 rgba(255,255,255,0.05)"
                : "0 4px 20px rgba(255, 23, 68, 0.15), inset 0 1px 0 rgba(255,255,255,0.05)",
              animation: "trade-enter 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
            }}
          >
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              fontSize: "10px",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              opacity: 0.8,
              marginBottom: "4px",
              color: "#fff",
            }}>
              <span style={{ display: "flex", alignItems: "center", gap: "4px", fontWeight: 700 }}>
                {isUp 
                  ? <TrendingUp style={{ width: 12, height: 12, color: "var(--iq-green)" }} />
                  : <TrendingDown style={{ width: 12, height: 12, color: "var(--iq-red)" }} />}
                {t.type === "binary" ? "DIGITAL" : "CFD"}
              </span>
              <span style={{ fontSize: "11px", fontWeight: 600 }}>${Number(t.amount).toFixed(0)}</span>
            </div>
            
            <div style={{
              fontSize: "20px",
              fontWeight: 800,
              lineHeight: 1.1,
              marginBottom: "6px",
              color: winning ? "var(--iq-green)" : "var(--iq-red)",
              textShadow: winning ? "0 0 12px rgba(0,230,118,0.5)" : "0 0 12px rgba(255,23,68,0.5)",
            }} className={winning ? "profit-glow" : "loss-glow"}>
              {pnl >= 0 ? "+" : ""}${pnl.toFixed(2)}
            </div>

            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              fontSize: "10px",
              opacity: 0.9,
              color: "var(--iq-text-bright)",
            }}>
              <span>{roi >= 0 ? "+" : ""}{roi.toFixed(1)}%</span>
              {t.type === "binary" && t.expires_at && (
                <span style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "3px",
                  color: "var(--iq-orange)",
                  fontWeight: 600,
                }}>
                  <Clock style={{ width: 11, height: 11 }} />
                  {remaining}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TradeOverlay({ trades, tickers, current, assetsMap }) {
  const [, force] = useState(0);
  useEffect(() => {
    const i = setInterval(() => force((v) => v + 1), 200);
    return () => clearInterval(i);
  }, []);

  if (!trades.length) return null;
  const price = tickers[current?.symbol]?.price;

  return (
    <div className="hidden lg:flex iq-trade-overlay" data-testid="trade-overlay">
      {trades.map((t) => {
        let pnl = 0;
        if (price != null) {
          if (t.type === "cfd") {
            const ret = (price - t.entry_price) / t.entry_price;
            const signed = t.direction === "sell" ? -ret : ret;
            pnl = signed * t.amount * (t.leverage || 1);
            if (pnl < -t.amount) pnl = -t.amount;
          } else {
            const win = (t.direction === "call" && price > t.entry_price) || (t.direction === "put" && price < t.entry_price);
            const payout = assetsMap[t.symbol]?.payout ?? 85;
            pnl = win ? t.amount * (payout / 100) : -t.amount;
          }
        }
        const isUrgent = t.type === "binary" && t.expires_at && (new Date(t.expires_at).getTime() - Date.now()) < 10000;
        const remaining = timeLeft(t.expires_at);
        const totalSec = t.duration_sec || 60;
        const elapsed = t.expires_at ? Math.max(0, totalSec - Math.ceil((new Date(t.expires_at).getTime() - Date.now()) / 1000)) : 0;
        const pct = Math.max(0, Math.min(100, (elapsed / totalSec) * 100));
        const dirIsUp = t.direction === "call" || t.direction === "buy";

        return (
          <React.Fragment key={t.id}>
            {/* Trade direction badge */}
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{
                width: 28, height: 28, borderRadius: "50%",
                background: dirIsUp ? "rgba(0,230,118,0.15)" : "rgba(255,23,68,0.15)",
                border: `1px solid ${dirIsUp ? "rgba(0,230,118,0.4)" : "rgba(255,23,68,0.4)"}`,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                {dirIsUp
                  ? <TrendingUp style={{ width: 14, height: 14, color: "var(--iq-green)" }} />
                  : <TrendingDown style={{ width: 14, height: 14, color: "var(--iq-red)" }} />}
              </div>
              <div>
                <div style={{ fontSize: 10, color: "var(--iq-text-dim)", textTransform: "uppercase", letterSpacing: "0.1em" }}>Yön</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: dirIsUp ? "var(--iq-green)" : "var(--iq-red)" }}>
                  {dirIsUp ? "YÜKSEK" : "AŞAĞI"}
                </div>
              </div>
            </div>

            {/* Countdown with circular progress */}
            {t.type === "binary" && t.expires_at && (
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }} data-testid={`overlay-timer-${t.id}`}>
                {/* Radial countdown */}
                <div style={{ position: "relative", width: 40, height: 40 }}>
                  <svg width="40" height="40" style={{ transform: "rotate(-90deg)", position: "absolute" }}>
                    <circle cx="20" cy="20" r="16" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="3" />
                    <circle
                      cx="20" cy="20" r="16" fill="none"
                      stroke={isUrgent ? "var(--iq-red)" : "var(--iq-orange)"}
                      strokeWidth="3"
                      strokeDasharray={`${((100 - pct) / 100) * 100.53} 100.53`}
                      strokeLinecap="round"
                      style={{
                        transition: "stroke-dasharray 0.2s linear, stroke 0.3s",
                        filter: `drop-shadow(0 0 ${isUrgent ? 6 : 3}px ${isUrgent ? "var(--iq-red)" : "var(--iq-orange)"})`,
                      }}
                    />
                  </svg>
                  <div style={{
                    position: "absolute", inset: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <Clock style={{ width: 12, height: 12, color: isUrgent ? "var(--iq-red)" : "var(--iq-orange)" }} />
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 9, color: "var(--iq-text-dim)", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 1 }}>
                    VADE SÜRESİ
                  </div>
                  <span
                    className={isUrgent ? "countdown-urgent" : ""}
                    style={{
                      fontSize: "22px", fontWeight: 800,
                      fontFamily: "'JetBrains Mono', monospace",
                      color: isUrgent ? "var(--iq-red)" : "var(--iq-orange)",
                      display: "block", lineHeight: 1,
                    }}
                  >
                    {remaining}
                  </span>
                </div>
              </div>
            )}

            {/* Investment amount */}
            <div data-testid={`overlay-amount-${t.id}`} style={{ textAlign: "center" }}>
              <div style={{ fontSize: 9, color: "var(--iq-text-dim)", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 2 }}>
                YATIRIM
              </div>
              <span style={{
                fontSize: "18px", fontWeight: 800, fontFamily: "'JetBrains Mono', monospace",
                color: "#fff",
              }}>
                ${Number(t.amount).toLocaleString("en-US", { minimumFractionDigits: 2 })}
              </span>
            </div>

            {/* Divider */}
            <div style={{ width: 1, height: 32, background: "var(--iq-border-bright)", flexShrink: 0 }} />

            {/* Expected profit - animated */}
            <div data-testid={`overlay-pnl-${t.id}`} style={{ textAlign: "center" }}>
              <div style={{ fontSize: 9, color: "var(--iq-text-dim)", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 2 }}>
                TAHMİNİ GELİR
              </div>
              <span style={{
                fontSize: "18px", fontWeight: 800, fontFamily: "'JetBrains Mono', monospace",
                color: pnl >= 0 ? "var(--iq-green)" : "var(--iq-red)",
                display: "block", lineHeight: 1,
              }} className={pnl >= 0 ? "profit-glow" : "loss-glow"}>
                {pnl >= 0 ? "+" : ""}${pnl.toFixed(2)}
              </span>
              {/* Mini progress bar */}
              {t.type === "binary" && (
                <div style={{ marginTop: 4, height: 2, background: "rgba(255,255,255,0.05)", borderRadius: 1, width: 80 }}>
                  <div style={{
                    height: "100%", borderRadius: 1,
                    width: `${pct}%`,
                    background: pnl >= 0
                      ? "linear-gradient(90deg, var(--iq-green), #00ff88)"
                      : "linear-gradient(90deg, var(--iq-red), #ff6b6b)",
                    transition: "width 0.2s linear",
                  }} />
                </div>
              )}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}
